import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import Link from "@mui/material/Link";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import { useTheme } from "@mui/material/styles";
import ThumbUpOutlined from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownOutlined from "@mui/icons-material/ThumbDownOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";

import Button from "../../components/Button";
import TableComponent from "../TableComponent";
import EditDatesModal from "./EditDatesModal";

import { openDownloadWindow } from "../../api";
import { formatDate } from "../../services/formatters";

interface PendingCertificate {
  id: number;
  crewId: number;
  userId: number;
  username: string;
  userEmail: string;
  courseId: number;
  name: string;
  completedDate: string | null;
  validPeriod: string | null;
  comments: string | null;
  fileName: string | null;
  isCustom: boolean;
  status: string;
}

interface PendingCertificateListProps {
  pending: PendingCertificate[];
  resolve: (item: PendingCertificate, status: string) => void;
  onUpdate?: () => void;
}

const PendingCertificateList: FC<PendingCertificateListProps> = ({ pending, resolve, onUpdate }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const onNotFound = () => enqueueSnackbar(t("orders.document_not_found"), { variant: "error" });
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<PendingCertificate | null>(null);

  const handleEdit = (item: PendingCertificate) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const handleSuccess = () => {
    if (onUpdate) {
      onUpdate();
    }
  };

  return (
    <>
      <TableComponent
        pagination
        search={["name", "username", "userEmail", "userId", "courseId", "comments"]}
        items={pending}
        header={[
          { title: t("pending_certificates.member_fullname_email"), style: { width: "20%" } },
          { title: t("pending_certificates.course_name"), style: { width: "20%" } },
          { title: t("pending_certificates.completed_date"), style: { width: "8%" } },
          { title: t("pending_certificates.validity_period"), style: { width: "8%" } },
          { title: t("pending_certificates.comments"), style: { width: "22%" } },
          { title: t("pending_certificates.certificate_file"), style: { width: "10%" } },
          { title: t("pending_certificates.action"), style: { width: "12%" } },
        ]}
        renderChildren={(item: PendingCertificate) => (
          <TableRow key={item.id}>
            <TableCell>
              ({item.userId}) {item.username} / {item.userEmail}
            </TableCell>
            <TableCell>
              ({item.isCustom ? `cc_${item.courseId}` : `${item.courseId}`}) {item.name}
            </TableCell>
            <TableCell>{formatDate(item.completedDate)}</TableCell>
            <TableCell>{item.validPeriod ? formatDate(item.validPeriod) : t("certificate.never_expires")}</TableCell>
            <TableCell>{item.comments || ""}</TableCell>
            <TableCell>
              {item.fileName && (
                <Link component="button" onClick={() => openDownloadWindow(item, item.userId, undefined, onNotFound)}>
                  {item.fileName}
                </Link>
              )}
            </TableCell>
            <TableCell>
              <Button
                title={t("pending_certificates.edit_dates")}
                icon={<EditOutlined sx={{ color: theme.palette.info.main }} />}
                onClick={() => handleEdit(item)}
              />
              <Button
                title={t("pending_certificates.approve_certificate")}
                icon={<ThumbUpOutlined sx={{ color: theme.palette.success.main }} />}
                onClick={() => resolve(item, "approve")}
              />
              <Button
                title={t("pending_certificates.decline_certificate")}
                icon={<ThumbDownOutlined sx={{ color: theme.palette.error.main }} />}
                onClick={() => resolve(item, "decline")}
              />
            </TableCell>
          </TableRow>
        )}
      />

      <EditDatesModal open={modalOpen} certificate={editingItem} onClose={handleCloseModal} onSuccess={handleSuccess} />
    </>
  );
};

export default PendingCertificateList;
